import * as React from 'react';
import HTMLParser from 'html-react-parser';
import { getFormDefinitionV1, submitFormEntryV1 } from '@/services/platform/api/cms/cms';
import type {
  FormDefinitionResponse,
  FormField,
  FormCondition,
  FormSubmissionRequest,
  FormSubmissionResponse,
} from '@/services/platform/api/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FormField as FormFieldWrapper } from '@/components/ui/form-field';
import { Heading } from '@/components/ui/heading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormValues = Record<string, string | string[] | boolean>;
type FormErrors = Record<string, string>;
type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
type ResolvedFieldType =
  | 'hidden'
  | 'titleDescription'
  | 'richText'
  | 'unsupportedCaptcha'
  | 'checkbox'
  | 'checkboxList'
  | 'select'
  | 'radio'
  | 'textarea'
  | 'date'
  | 'file'
  | 'password'
  | 'text';

interface UmbracoFormProps {
  instanceId: string;
  formId: string;
  /** Pre-fetched definition — skips the client-side fetch when provided */
  formDefinition?: FormDefinitionResponse;
  contentId?: string;
  culture?: string;
  onSuccess?: (response: FormSubmissionResponse) => void;
  onError?: (error: unknown) => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Conditional logic helpers (pure — no hooks)
// ---------------------------------------------------------------------------

function evaluateRule(
  rule: { field: string; operator: string; value?: string | null },
  values: FormValues,
  guidToAlias: Map<string, string>
): boolean {
  const alias = guidToAlias.get(rule.field);
  if (!alias) return false;

  const rawValue = values[alias];
  const fieldValue = Array.isArray(rawValue)
    ? rawValue.join(',')
    : rawValue === true
      ? 'true'
      : rawValue === false
        ? 'false'
        : (rawValue ?? '');

  const ruleValue = rule.value ?? '';
  const op = rule.operator?.toLowerCase();

  switch (op) {
    case 'is':
      return fieldValue === ruleValue;
    case 'isnot':
      return fieldValue !== ruleValue;
    case 'greaterthen':
      return parseFloat(fieldValue) > parseFloat(ruleValue);
    case 'lessthen':
      return parseFloat(fieldValue) < parseFloat(ruleValue);
    case 'contains':
      return fieldValue.includes(ruleValue);
    case 'startswith':
      return fieldValue.startsWith(ruleValue);
    case 'endswith':
      return fieldValue.endsWith(ruleValue);
    default:
      return false;
  }
}

function isFieldVisible(field: FormField, values: FormValues, guidToAlias: Map<string, string>): boolean {
  const condition = field.condition as FormCondition | null | undefined;
  if (!condition || !condition.rules?.length) return true;

  const ruleResults = condition.rules.map(r => evaluateRule(r as any, values, guidToAlias));

  const satisfied =
    condition.logicType?.toLowerCase() === 'any' ? ruleResults.some(Boolean) : ruleResults.every(Boolean);

  return condition.actionType?.toLowerCase() === 'hide' ? !satisfied : satisfied;
}

// ---------------------------------------------------------------------------
// Flat-field helpers
// ---------------------------------------------------------------------------

function flattenFields(formDef: FormDefinitionResponse): FormField[] {
  return (formDef.pages ?? [])
    .flatMap(p => (p.fieldsets ?? []).flatMap(fs => (fs.columns ?? []).flatMap(c => c.fields ?? [])))
    .filter((f): f is FormField => f != null);
}

function buildGuidToAlias(fields: FormField[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const f of fields) {
    if (f.id && f.alias) {
      map.set(String(f.id), f.alias);
    }
  }
  return map;
}

function hasValue(v: string | string[] | boolean | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.length > 0;
  return v.trim() !== '';
}

function normalizeFieldTypeName(typeName: string | null | undefined): string {
  return (typeName ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/_/g, ' ');
}

function resolveFieldType(field: FormField): ResolvedFieldType {
  const normalizedTypeName = normalizeFieldTypeName(field.type?.name);

  switch (normalizedTypeName) {
    case 'hidden':
    case 'hiddenfield':
      return 'hidden';
    case 'title and description':
    case 'titleanddescription':
      return 'titleDescription';
    case 'rich text':
    case 'richtext':
      return 'richText';
    case 'recaptcha v2':
    case 'recaptcha2':
    case 'recaptcha':
    case 'recaptcha v3 with score':
    case 'recaptcha v3':
    case 'recaptchav3withscore':
    case 'recaptchav3':
    case 'recaptcha enterprise with score':
    case 'recaptcha enterprise':
    case 'recaptchaenterprisewithscore':
    case 'recaptchaenterprise':
      return 'unsupportedCaptcha';
    case 'checkbox':
    case 'data consent':
    case 'dataconsent':
      return 'checkbox';
    case 'multiple choice':
    case 'checkboxlist':
      return 'checkboxList';
    case 'dropdown':
    case 'dropdownlist':
      return 'select';
    case 'single choice':
    case 'radiobuttonlist':
      return 'radio';
    case 'long answer':
    case 'longanswer':
    case 'textarea':
      return 'textarea';
    case 'date':
    case 'datepicker':
    case 'datepickerfieldtype':
      return 'date';
    case 'file upload':
    case 'fileupload':
      return 'file';
    case 'password':
      return 'password';
    case 'short answer':
    case 'shortanswer':
    case 'textfield':
    case 'text':
    case 'email':
    default:
      return normalizedTypeName === 'password' ? 'password' : 'text';
  }
}

function getRichTextMarkup(field: FormField): string {
  const settings = field.settings ?? {};
  const markup =
    settings.markup ??
    settings.html ??
    settings.text ??
    settings.caption ??
    field.helpText ??
    field.caption ??
    '';

  return typeof markup === 'string' ? markup : '';
}

function sanitizeRichTextMarkup(markup: string): string {
  if (!markup) return '';

  return markup
    .replace(/<\s*(script|style|iframe|object|embed|form|meta|link|base)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[^'"]*\2/gi, '')
    .replace(/\s(href|src)\s*=\s*javascript:[^\s>]+/gi, '');
}

function serializeFieldValue(
  field: FormField,
  value: string | string[] | boolean | undefined
): string | string[] | undefined {
  const resolvedType = resolveFieldType(field);

  if (value === undefined) {
    return undefined;
  }

  if (resolvedType === 'checkbox') {
    return value === true ? 'true' : undefined;
  }

  if (resolvedType === 'checkboxList') {
    return Array.isArray(value) ? value : undefined;
  }

  if (resolvedType === 'file') {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : undefined;
  }

  return value;
}

// ---------------------------------------------------------------------------
// Field renderer (internal)
// ---------------------------------------------------------------------------

interface FieldRendererProps {
  field: FormField;
  value: string | string[] | boolean | undefined;
  error: string | undefined;
  onChange: (alias: string, value: string | string[] | boolean) => void;
}

function FieldRenderer({ field, value, error, onChange }: FieldRendererProps) {
  const typeName = field.type?.name ?? '';
  const resolvedType = resolveFieldType(field);
  const alias = field.alias ?? '';
  const id = `field-${alias}`;
  const strVal = typeof value === 'string' ? value : '';
  const captionStr = field.caption ?? undefined;
  const helpTextStr = field.helpText ?? undefined;
  const placeholderStr = field.placeholder ?? undefined;

  if (resolvedType === 'hidden') {
    return (
      <Input
        type="hidden"
        id={id}
        name={alias}
        value={strVal}
        onChange={e => onChange(alias, e.target.value)}
      />
    );
  }

  if (resolvedType === 'titleDescription') {
    return (
      <div data-slot="form-title-description" className="flex flex-col gap-1">
        {field.caption && (
          <Heading as="h3" className="text-base font-semibold">
            {field.caption}
          </Heading>
        )}
        {field.helpText && <p className="text-muted-foreground text-sm">{field.helpText}</p>}
      </div>
    );
  }

  if (resolvedType === 'richText') {
    const markup = sanitizeRichTextMarkup(getRichTextMarkup(field));
    if (!markup) return null;

    return (
      <div data-slot="form-rich-text" className="prose max-w-none text-sm">
        {HTMLParser(markup)}
      </div>
    );
  }

  if (resolvedType === 'unsupportedCaptcha') {
    if (typeof console !== 'undefined') {
      console.warn(
        `[UmbracoForm] field type "${typeName}" for "${alias}" skipped — not supported in headless renderer`
      );
    }
    return null;
  }

  if (resolvedType === 'checkbox') {
    const checked = value === true || value === 'true';
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            checked={checked}
            onCheckedChange={(v: boolean | 'indeterminate') => onChange(alias, Boolean(v))}
            error={!!error}
          />
          {field.placeholder && (
            <Label htmlFor={id} className="font-inter font-normal">
              {field.placeholder}
            </Label>
          )}
        </div>
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'checkboxList') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <div className="flex flex-col gap-2">
          {(field.preValues ?? []).map(pv => {
            const pvValue = pv.value ?? '';
            const itemId = `${id}-${pvValue}`;
            const checked = selected.includes(pvValue);
            return (
              <div key={pvValue} className="flex items-center gap-2">
                <Checkbox
                  id={itemId}
                  checked={checked}
                  onCheckedChange={(v: boolean | 'indeterminate') => {
                    const next = v ? [...selected, pvValue] : selected.filter((s: string) => s !== pvValue);
                    onChange(alias, next);
                  }}
                  error={!!error}
                />
                <Label htmlFor={itemId} className="font-inter font-normal">
                  {pv.caption ?? pvValue}
                </Label>
              </div>
            );
          })}
        </div>
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'select') {
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <Select value={strVal} onValueChange={(v: string) => onChange(alias, v)}>
          <SelectTrigger id={id} aria-invalid={error ? 'true' : undefined} className="w-full">
            <SelectValue placeholder={placeholderStr ?? 'Select…'} />
          </SelectTrigger>
          <SelectContent>
            {(field.preValues ?? []).map(pv => {
              const pvValue = pv.value ?? '';
              return (
                <SelectItem key={pvValue} value={pvValue}>
                  {pv.caption ?? pvValue}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'radio') {
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <RadioGroup value={strVal} onValueChange={(v: string) => onChange(alias, v)}>
          {(field.preValues ?? []).map(pv => {
            const pvValue = pv.value ?? '';
            const itemId = `${id}-${pvValue}`;
            return (
              <div key={pvValue} className="flex items-center gap-2">
                <RadioGroupItem id={itemId} value={pvValue} error={!!error} />
                <Label htmlFor={itemId} className="font-inter font-normal">
                  {pv.caption ?? pvValue}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'textarea') {
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <Textarea
          id={id}
          name={alias}
          placeholder={placeholderStr}
          value={strVal}
          onChange={e => onChange(alias, e.target.value)}
          error={!!error}
        />
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'date') {
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <Input
          type="date"
          id={id}
          name={alias}
          value={strVal}
          onChange={e => onChange(alias, e.target.value)}
          error={!!error}
        />
      </FormFieldWrapper>
    );
  }

  if (resolvedType === 'file') {
    const opts = field.fileUploadOptions;
    const accept =
      opts && !opts.allowAllUploadExtensions && opts.allowedUploadExtensions?.length
        ? opts.allowedUploadExtensions.map(ext => (ext.startsWith('.') ? ext : `.${ext}`)).join(',')
        : undefined;
    return (
      <FormFieldWrapper
        id={id}
        label={captionStr}
        helpText={helpTextStr}
        error={error}
        required={field.required}
      >
        <Input
          type="file"
          id={id}
          name={alias}
          accept={accept}
          multiple={opts?.allowMultipleFileUploads ?? false}
          disabled
          error={!!error}
        />
        <p className="text-muted-foreground text-xs">
          File uploads are not supported by this headless renderer yet.
        </p>
      </FormFieldWrapper>
    );
  }

  const inputType =
    resolvedType === 'password'
      ? 'password'
      : normalizeFieldTypeName(typeName) === 'email'
        ? 'email'
        : 'text';
  return (
    <FormFieldWrapper
      id={id}
      label={captionStr}
      helpText={helpTextStr}
      error={error}
      required={field.required}
    >
      <Input
        type={inputType}
        id={id}
        name={alias}
        placeholder={placeholderStr}
        value={strVal}
        onChange={e => onChange(alias, e.target.value)}
        error={!!error}
      />
    </FormFieldWrapper>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function UmbracoForm({
  instanceId,
  formId,
  formDefinition,
  contentId,
  culture,
  onSuccess,
  onError,
  className,
}: UmbracoFormProps) {
  const [formDef, setFormDef] = React.useState<FormDefinitionResponse | null>(formDefinition ?? null);
  const [loading, setLoading] = React.useState(!formDefinition);
  const [submitState, setSubmitState] = React.useState<SubmitState>('idle');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [values, setValues] = React.useState<FormValues>({});
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [serverError, setServerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (formDefinition) return;

    let cancelled = false;
    setLoading(true);

    getFormDefinitionV1(
      instanceId,
      formId,
      contentId || culture
        ? {
            ...(contentId ? { contentId: contentId as unknown as string } : {}),
            ...(culture ? { culture } : {}),
          }
        : undefined
    )
      .then(res => {
        if (cancelled) return;
        if (res.status === 200) {
          setFormDef(res.data as FormDefinitionResponse);
        }
      })
      .catch(err => {
        if (cancelled) return;
        onError?.(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceId, formId, contentId, culture]);

  const allFields = React.useMemo(() => (formDef ? flattenFields(formDef) : []), [formDef]);

  const guidToAlias = React.useMemo(() => buildGuidToAlias(allFields), [allFields]);

  function validateFields(fields: FormField[]): FormErrors {
    const errs: FormErrors = {};
    for (const field of fields) {
      const alias = field.alias ?? '';
      if (!alias) continue;
      const resolvedType = resolveFieldType(field);
      if (
        resolvedType === 'hidden' ||
        resolvedType === 'titleDescription' ||
        resolvedType === 'richText' ||
        resolvedType === 'unsupportedCaptcha'
      ) {
        continue;
      }
      if (!isFieldVisible(field, values, guidToAlias)) continue;
      if (resolvedType === 'file') {
        errs[alias] = 'File uploads are not supported yet.';
        continue;
      }
      if (field.required && !hasValue(values[alias])) {
        errs[alias] = field.requiredErrorMessage ?? `${field.caption ?? alias} is required`;
      }
    }
    return errs;
  }

  function currentPageFields(): FormField[] {
    if (!formDef) return [];
    const page = (formDef.pages ?? [])[currentPage];
    if (!page) return [];
    return (page.fieldsets ?? [])
      .flatMap(fs => (fs.columns ?? []).flatMap(c => c.fields ?? []))
      .filter((f): f is FormField => f != null);
  }

  function handleChange(alias: string | null | undefined, value: string | string[] | boolean) {
    if (!alias) return;
    setValues(prev => ({ ...prev, [alias]: value }));
    if (errors[alias]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[alias];
        return next;
      });
    }
  }

  function handleNext() {
    const pageErrors = validateFields(currentPageFields());
    if (Object.keys(pageErrors).length > 0) {
      setErrors(pageErrors);
      return;
    }
    setErrors({});
    setCurrentPage(p => p + 1);
  }

  function handleBack() {
    setErrors({});
    setCurrentPage(p => p - 1);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formDef) return;

    const pageErrors = validateFields(currentPageFields());
    if (Object.keys(pageErrors).length > 0) {
      setErrors(pageErrors);
      return;
    }

    setSubmitState('submitting');
    setServerError(null);

    const submissionValues: Record<string, unknown> = {};
    for (const field of allFields) {
      const fieldAlias = field.alias ?? '';
      if (!fieldAlias) continue;
      const resolvedType = resolveFieldType(field);
      if (
        resolvedType === 'hidden' ||
        resolvedType === 'titleDescription' ||
        resolvedType === 'richText' ||
        resolvedType === 'unsupportedCaptcha'
      ) {
        continue;
      }
      if (!isFieldVisible(field, values, guidToAlias)) continue;
      const serializedValue = serializeFieldValue(field, values[fieldAlias]);
      if (serializedValue !== undefined) {
        submissionValues[fieldAlias] = serializedValue;
      }
    }

    const body: FormSubmissionRequest = {
      values: submissionValues as Record<string, object>,
      ...(contentId ? { contentId: contentId as unknown as string } : {}),
      ...(culture ? { culture } : {}),
    };

    try {
      const res = await submitFormEntryV1(instanceId, formId, body);

      if (res.status === 202) {
        setSubmitState('success');
        const successData = res.data as FormSubmissionResponse;
        onSuccess?.(successData);
        return;
      }

      if (res.status === 422) {
        const errData = res.data as { error?: { validationErrors?: Record<string, string[]> } };
        const validationErrors = errData?.error?.validationErrors ?? {};
        const mapped: FormErrors = {};
        for (const [alias, msgs] of Object.entries(validationErrors)) {
          mapped[alias] = Array.isArray(msgs) ? msgs[0] : String(msgs);
        }
        setErrors(mapped);
        setSubmitState('idle');
        return;
      }

      setServerError('An error occurred. Please try again.');
      setSubmitState('error');
      onError?.(new Error('Unhandled form submission response.'));
    } catch (err) {
      setServerError('An error occurred. Please try again.');
      setSubmitState('error');
      onError?.(err);
    }
  }

  if (loading) {
    return (
      <div data-slot="umbraco-form-loading" className="flex items-center justify-center py-8">
        <div className="border-primary size-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!formDef) return null;

  if (submitState === 'success') {
    return (
      <div
        data-slot="umbraco-form-success"
        className="bg-muted rounded-lg p-6 text-center"
        role="status"
        aria-live="polite"
      >
        <p className="text-sm">{formDef.messageOnSubmit ?? 'Thank you for your submission.'}</p>
      </div>
    );
  }

  const pages = formDef.pages ?? [];
  const page = pages[currentPage];
  const isLastPage = currentPage === pages.length - 1;
  const isFirstPage = currentPage === 0;

  const nextLabel = formDef.nextLabel ?? 'Next';
  const previousLabel = formDef.previousLabel ?? 'Back';
  const submitLabel = formDef.submitLabel ?? 'Submit';

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form data-slot="umbraco-form" onSubmit={handleSubmit} noValidate className={className}>
      {page.caption && (
        <Heading as="h2" className="mb-4 text-lg font-semibold">
          {page.caption}
        </Heading>
      )}

      {formDef.showValidationSummary && hasErrors && (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mb-4 rounded-lg border p-3 text-sm"
        >
          <ul className="list-disc pl-4">
            {Object.values(errors).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {serverError && (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mb-4 rounded-lg border p-3 text-sm"
        >
          {serverError}
        </div>
      )}

      <div className="flex flex-col gap-6">
        {(page.fieldsets ?? []).map(fieldset => (
          <fieldset key={String(fieldset.id)} className="flex flex-col gap-2">
            {fieldset.caption && <legend className="mb-2 text-sm font-medium">{fieldset.caption}</legend>}
            <div className="grid grid-cols-12 gap-4">
              {(fieldset.columns ?? []).map((col, ci) => {
                const span = Math.max(1, Math.min(12, col.width ?? 12));
                return (
                  <div key={ci} style={{ gridColumn: `span ${span}` }} className="flex flex-col gap-3">
                    {(col.fields ?? [])
                      .filter((f): f is FormField => f != null)
                      .filter(f => isFieldVisible(f, values, guidToAlias))
                      .map(field => (
                        <FieldRenderer
                          key={String(field.id)}
                          field={field}
                          value={values[field.alias ?? '']}
                          error={errors[field.alias ?? '']}
                          onChange={handleChange}
                        />
                      ))}
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-2">
        {!isFirstPage && (
          <Button type="button" variant="secondary-outline" onClick={handleBack}>
            {previousLabel}
          </Button>
        )}
        {isLastPage ? (
          <Button type="submit" disabled={submitState === 'submitting'}>
            {submitState === 'submitting' ? 'Submitting…' : submitLabel}
          </Button>
        ) : (
          <Button type="button" onClick={handleNext}>
            {nextLabel}
          </Button>
        )}
      </div>
    </form>
  );
}
